import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Search, LayoutDashboard, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const links = [
  { name: "Home", to: "/" },
  { name: "Buy", to: "/properties?listing_type=Buy" },
  { name: "Rent", to: "/properties?listing_type=Rent" },
  { name: "Services", to: "/services" },
  { name: "Gallery", to: "/gallery" },
  { name: "About", to: "/about" },
  { name: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <>
      <motion.nav
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4"
      >
        <div className="flex items-center justify-between w-full max-w-7xl px-5 py-2.5 glass rounded-full shadow-soft">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
            <img src="/logo.png" alt="my Property Pune" className="h-9 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            {links.map((l) => (
              <Link
                key={l.name}
                to={l.to}
                data-testid={`nav-link-${l.name.toLowerCase()}`}
                className="text-sm text-navy hover:text-brand-500 font-medium transition-colors relative group"
              >
                {l.name}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-brand-500 transition-all group-hover:w-full" />
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2.5 shrink-0">
            <Link
              to="/properties"
              data-testid="nav-search-property-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-full hover:bg-brand-600 transition-colors hover:scale-105 active:scale-95 duration-200"
            >
              <Search className="w-4 h-4" /> Search Property
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 border border-neutral-300 rounded-full transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
                <button onClick={onLogout} data-testid="nav-logout-btn" title="Logout"
                  className="p-2 text-neutral-500 hover:text-neutral-900 transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-btn"
                className="px-4 py-2 text-sm text-neutral-700 hover:text-neutral-900 border border-neutral-300 rounded-full transition-colors">
                Login
              </Link>
            )}
          </div>

          <button className="lg:hidden text-neutral-800" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </motion.nav>

      <motion.div
        initial={false}
        animate={{ opacity: open ? 1 : 0, pointerEvents: open ? "auto" : "none" }}
        className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-5 bg-white/97 backdrop-blur-xl lg:hidden overflow-y-auto py-24"
      >
        {links.map((l) => (
          <Link key={l.name} to={l.to} onClick={() => setOpen(false)}
            className="text-2xl font-bold text-neutral-800 hover:text-brand-500">
            {l.name}
          </Link>
        ))}
        <Link to="/properties" onClick={() => setOpen(false)}
          className="flex items-center gap-2 px-6 py-3 text-lg font-bold text-white bg-brand-500 rounded-full">
          <Search className="w-5 h-5" /> Search Property
        </Link>
        {user ? (
          <>
            <Link to="/dashboard" onClick={() => setOpen(false)} className="text-xl text-neutral-700">Dashboard</Link>
            <button onClick={onLogout} className="text-xl text-neutral-500">Logout</button>
          </>
        ) : (
          <Link to="/login" onClick={() => setOpen(false)} className="text-xl text-neutral-700">Login</Link>
        )}
      </motion.div>
    </>
  );
}
